import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ORPCError } from "@orpc/client";

import { orpc } from "@/client";
import { messagingConnection } from "@/lib/messaging-connection";
import { MessageCache } from "@/lib/message-cache";

class MessagingSubscription {
	private cursor: string | null = null;
	private readonly stop = new AbortController();

	constructor(private client: QueryClient) {}

	close() {
		this.stop.abort();
		messagingConnection.set(false);
	}

	async run() {
		let attempts = 0;

		while (!this.cancelled()) {
			const success = await this.consume()
				.then(() => true)
				.catch((error: unknown) => {
					if (
						error instanceof ORPCError &&
						(error.code === "UNAUTHORIZED" || error.code === "PASSWORD_CHANGE_REQUIRED")
					) {
						this.stop.abort();
					}

					return false;
				});
			messagingConnection.set(false);

			if (this.cancelled()) {
				return;
			}

			attempts = success ? 0 : attempts + 1;
			await this.pause(Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5)) + Math.random() * 500);
		}
	}

	private cancelled() {
		return this.stop.signal.aborted;
	}

	private async consume() {
		const events = await orpc.realtime.events.call(
			{ cursor: this.cursor },
			{ signal: this.stop.signal },
		);

		for await (const event of events) {
			if (this.cancelled()) {
				return;
			}

			messagingConnection.set(true);

			if (event.kind === "reset") {
				await MessageCache.for(this.client).all(true);
				await Promise.all([
					this.client.invalidateQueries({ queryKey: orpc.cockpit.key() }),
					this.client.invalidateQueries({ queryKey: orpc.draft.context.key() }),
					this.client.invalidateQueries({ queryKey: orpc.people.detail.key() }),
					this.client.invalidateQueries({ queryKey: orpc.timeline.forPerson.key() }),
				]);
				await this.refreshLists();
			} else if (event.kind === "ready") {
				await MessageCache.for(this.client).all();
			} else if (event.kind === "changes") {
				const people = new Set(event.changes.map((change) => change.person_id));

				for (const personId of people) {
					await this.refreshPerson(
						personId,
						event.changes.some(
							(change) => change.person_id === personId && change.topic === "messages",
						),
					);
				}

				await this.refreshLists();
			}

			if (event.kind === "heartbeat") {
				await this.client.invalidateQueries({ queryKey: orpc.realtime.health.key() });
				await this.client.invalidateQueries({ queryKey: orpc.system.jobs.key() });
			}

			this.cursor = event.cursor;
		}
	}

	private async refreshPerson(personId: number, messages: boolean) {
		await this.client.invalidateQueries({
			queryKey: orpc.people.detail.key({ input: { id: personId } }),
		});
		await this.client.invalidateQueries({
			queryKey: orpc.cockpit.productActivity.key({ input: { person_id: personId } }),
		});
		if (messages) {
			await MessageCache.for(this.client).person(personId);
			await this.client.invalidateQueries({
				queryKey: orpc.cockpit.files.key({ input: { person_id: personId } }),
			});
			await this.client.invalidateQueries({
				queryKey: orpc.cockpit.filesCount.key({ input: { person_id: personId } }),
			});
		}

		await Promise.all([
			this.client.invalidateQueries({
				queryKey: orpc.draft.context.key({ input: { person_id: personId } }),
			}),
			this.client.invalidateQueries({
				queryKey: orpc.cockpit.intelligence.key({ input: { person_id: personId } }),
			}),
			this.client.invalidateQueries({
				queryKey: orpc.cockpit.personActions.key({ input: { person_id: personId } }),
			}),
			this.client.invalidateQueries({
				queryKey: orpc.timeline.forPerson.key({ input: { person_id: personId } }),
			}),
		]);
	}

	private async refreshLists() {
		await Promise.all([
			this.client.invalidateQueries({ queryKey: orpc.messages.list.key() }),
			this.client.invalidateQueries({ queryKey: orpc.realtime.inbox.key() }),
			this.client.invalidateQueries({ queryKey: orpc.realtime.health.key() }),
			this.client.invalidateQueries({ queryKey: orpc.today.key() }),
		]);
	}

	private pause(ms: number) {
		return new Promise<void>((resolve) => {
			const done = () => {
				clearTimeout(timer);
				this.stop.signal.removeEventListener("abort", done);
				resolve();
			};
			const timer = setTimeout(done, ms);
			this.stop.signal.addEventListener("abort", done, { once: true });
		});
	}
}

export function useMessagingEvents() {
	const client = useQueryClient();

	useEffect(() => {
		const subscription = new MessagingSubscription(client);
		void subscription.run();
		return () => subscription.close();
	}, [client]);
}

export function useMessagingConnected() {
	return useSyncExternalStore(messagingConnection.subscribe, messagingConnection.snapshot);
}
